import { DataType, asISODateString, asISODateTimeString, type ISODateString, type ISODateTimeString } from '../../types/core.types';

/**
 * データ型推論モジュール
 * 値の配列から適切なデータ型を推論する
 */

// ブール値として解釈可能な文字列のマップ
const BOOLEAN_MAP: Record<string, boolean> = {
  'true': true,
  'false': false,
  '1': true,
  '0': false,
  'yes': true,
  'no': false,
  'y': true,
  'n': false,
};

/**
 * 値の配列からデータ型を推論
 * @param values 値の配列
 * @returns 推論されたデータ型
 */
export function inferColumnType(values: unknown[]): DataType {
  // null/undefined以外の値を取得
  const nonNullValues = values.filter(v => v !== null && v !== undefined && v !== '');

  if (nonNullValues.length === 0) {
    return 'string'; // 全てnullの場合はstring型として扱う
  }

  // ブール型の判定（最優先）
  if (allValuesAreBoolean(nonNullValues)) {
    return 'boolean';
  }

  // 数値型の判定
  const numericCheck = checkNumericType(nonNullValues);
  if (numericCheck !== null) {
    return numericCheck;
  }

  // 日付・日時型の判定
  const dateTimeCheck = checkDateTimeType(nonNullValues);
  if (dateTimeCheck !== null) {
    return dateTimeCheck;
  }

  // デフォルトは文字列型
  return 'string';
}

/**
 * すべての値がブール値として解釈可能かチェック
 */
function allValuesAreBoolean(values: unknown[]): boolean {
  return values.every(value => {
    if (typeof value === 'boolean') return true;

    const strValue = String(value).toLowerCase().trim();
    return strValue in BOOLEAN_MAP;
  });
}

/**
 * 値をブール値に変換
 */
export function toBoolean(value: unknown): boolean | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value;

  const strValue = String(value).toLowerCase().trim();
  return BOOLEAN_MAP[strValue] ?? null;
}

/**
 * 数値型をチェックし、適切な型を返す
 */
function checkNumericType(values: unknown[]): 'int64' | 'float64' | null {
  let hasFloat = false;

  for (const value of values) {
    const num = toNumber(value);
    if (num === null) {
      return null; // 数値に変換できない値がある
    }

    if (!Number.isInteger(num)) {
      hasFloat = true;
    }
  }

  return hasFloat ? 'float64' : 'int64';
}

/**
 * 値を数値に変換
 */
export function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;

  const strValue = String(value).trim();
  // カンマを除去（1,000 -> 1000）
  const cleanValue = strValue.replace(/,/g, '');
  const num = Number(cleanValue);

  return isNaN(num) ? null : num;
}

/**
 * 日付・日時型をチェックし、適切な型を返す
 */
function checkDateTimeType(values: unknown[]): 'date' | 'datetime' | null {
  let hasTime = false;
  let allDates = true;

  for (const value of values) {
    const dateInfo = parseDateTimeValue(value);
    if (!dateInfo) {
      allDates = false;
      break;
    }
    if (dateInfo.hasTime) {
      hasTime = true;
    }
  }

  if (!allDates) return null;
  return hasTime ? 'datetime' : 'date';
}

/**
 * 値を解析して日付情報を返す
 */
function parseDateTimeValue(value: unknown): { date: Date; hasTime: boolean } | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    const hasTime = value.getHours() !== 0 || value.getMinutes() !== 0 ||
                   value.getSeconds() !== 0 || value.getMilliseconds() !== 0;
    return { date: value, hasTime };
  }

  const strValue = String(value).trim();

  // 日付のみのパターン（時刻なし）
  const dateOnlyPatterns = [
    /^\d{4}-\d{2}-\d{2}$/,           // 2020-01-01
    /^\d{4}\/\d{2}\/\d{2}$/,         // 2020/01/01
    /^\d{2}\/\d{2}\/\d{4}$/,         // 01/01/2020
    /^\d{2}-\d{2}-\d{4}$/,           // 01-01-2020
  ];

  for (const pattern of dateOnlyPatterns) {
    if (pattern.test(strValue)) {
      const date = new Date(strValue);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        if (year >= 1900 && year <= 2100) {
          return { date, hasTime: false };
        }
      }
    }
  }

  // 日時パターン（時刻あり）
  if (/T\d{2}:\d{2}/.test(strValue) || /\d{2}:\d{2}/.test(strValue)) {
    const date = new Date(strValue);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      if (year >= 1900 && year <= 2100) {
        return { date, hasTime: true };
      }
    }
  }

  return null;
}

/**
 * 値をISO 8601形式のdatetime文字列に変換
 * @returns ISODateTimeString型の文字列、またはnull
 */
export function toISODateTimeString(value: unknown): ISODateTimeString | null {
  const date = toDateTime(value);
  return date ? asISODateTimeString(date.toISOString()) : null;
}

/**
 * 値をISO 8601形式のdate文字列（YYYY-MM-DD）に変換
 * @returns ISODateString型の文字列、またはnull
 */
export function toISODateString(value: unknown): ISODateString | null {
  const date = toDateTime(value);
  return date ? asISODateString(date.toISOString().split('T')[0]) : null;
}

/**
 * 値を日時に変換
 */
export function toDateTime(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return value;

  const strValue = String(value).trim();

  // ISO 8601形式をチェック
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/i.test(strValue)) {
    const date = new Date(strValue);
    if (isNaN(date.getTime())) return null;

    // 1900年から2100年の間の日付のみ有効とする
    const year = date.getFullYear();
    if (year >= 1900 && year <= 2100) {
      return date;
    }
    return null;
  }

  // その他の一般的な日付形式
  const date = new Date(strValue);
  if (!isNaN(date.getTime())) {
    // 1900年から2100年の間の日付のみ有効とする
    const year = date.getFullYear();
    if (year >= 1900 && year <= 2100) {
      return date;
    }
  }

  return null;
}

/**
 * 値の配列に対して適切な変換を行う
 */
export function convertValues(values: unknown[], dataType: DataType): (unknown | null)[] {
  switch (dataType) {
    case 'boolean':
      return values.map(v => toBoolean(v));
    case 'int64':
    case 'float64':
      return values.map(v => toNumber(v));
    case 'date':
      return values.map(v => {
        const date = toDateTime(v);
        // 日付のみの場合はYYYY-MM-DD形式で保存
        return date ? asISODateString(date.toISOString().split('T')[0]) : null;
      });
    case 'datetime':
      return values.map(v => {
        const date = toDateTime(v);
        return date ? asISODateTimeString(date.toISOString()) : null;
      });
    default:
      return values.map(v =>
        v === null || v === undefined || v === '' ? null : String(v)
      );
  }
}